import { nextBirthdayOnOrAfter } from "../_shared/birthdayFeedRows.ts";
import {
  isUniqueViolation,
  SINGLE_ROW_RETRY_LIMIT,
} from "../_shared/singleRowTaskTypes.ts";
import { normPerson } from "../_shared/birthdayTitle.ts";
import { zonedWallClockToUtc } from "../_shared/zonedTime.ts";

/**
 * #1721 — a non-kid birthday becomes a Birthday Reminder contact, on Pro.
 *
 * ⚠️ This deliberately relaxes Family Memory v1.2 §7's ownership guard, which
 * is why `create_scheduled_task`'s enum excludes `birthday_reminder`. Kasandra
 * signed that off on 2026-09-25 for THIS PATH ONLY. Do not widen it: no other
 * chat path may create a `birthday_reminder` task, and that enum stays as it
 * is.
 *
 * Shape is copied from the app's own `submitBirthdayReminder` (TasksTab), not
 * invented — same `display_name`, same `schedule_time`, same one-row-with-an-
 * array config, so a contact added from chat is indistinguishable from one she
 * typed.
 */

export interface BirthdayTaskClient {
  from: (table: string) => any;
}

export type BirthdayContactOutcome =
  | { ok: true; created: boolean; alreadyThere: boolean }
  | { ok: false; reason: "lookup" | "write" | "threw" };

/**
 * Days of warning for a contact Kin adds on her behalf.
 *
 * The app offers 0 / 1 / 3 / 7 and makes her choose. A chat turn has no
 * picker, so this is the default, and 7 is chosen rather than 0 for two
 * reasons: it is the app's own "1 week before" option, and it matches the
 * window the gift heads-up (#1666) already uses, so the two features agree
 * about when a birthday becomes actionable. She can change it in the app.
 */
export const DEFAULT_LEAD_DAYS = 7;

/**
 * The hour a birthday reminder lands on, HER time — and the single source for
 * both `schedule_time` and the initial `next_run_at`.
 *
 * ⚠️ These two MUST agree. After the first run the runner reschedules with
 * `nextRunAtOnDate(handlerDate, task.schedule_time, tz)` (#1338), so
 * `schedule_time` owns the hour forever after. An initial `next_run_at` at a
 * different hour is therefore not a one-off cosmetic difference: the first
 * reminder arrives at one time and every later one at another, with nothing
 * in the row explaining why. (Copilot on #1738.)
 *
 * 08:00 is the value the app writes into `schedule_time`, so a contact added
 * from chat recurs on exactly the same hour as one she typed.
 *
 * ⚠️ The FIRST run is where the two still differ, and not in this file's
 * favour to hide: the app's `birthdayRunAt` (TasksTab.tsx:5097-5103) computes
 * its initial `next_run_at` at 09:00 against the 08:00 it persists at
 * :5183-5186, so a contact SHE TYPES gets one 09:00 nudge and then moves to
 * 08:00 forever. That is the same defect corrected here, still live on the
 * app's own path, and it is a one-line change in a 23k-line shared file that
 * wants its own PR — deliberately not made from a chat-tool change.
 * (Copilot on #1748.) Until it lands, do not describe the two paths as
 * identical on the first run, because they are not.
 */
const REMINDER_HOUR = 8;
const SCHEDULE_TIME = `${String(REMINDER_HOUR).padStart(2, "0")}:00:00`;

/**
 * `next_run_at` for one contact: their next birthday, minus lead days, at
 * REMINDER_HOUR ON HER WALL CLOCK.
 *
 * ⚠️ The edge runtime is UTC. Treating that hour AS UTC puts a New York
 * mom's birthday reminder at 3am — the exact complaint behind #1651 and the
 * Aug 3 2026 outage. `zonedWallClockToUtc` does the two-pass DST correction.
 */
export function contactRunAt(
  birthday: string,
  leadDays: number,
  todayYmd: string,
  timeZone: string,
): Date | null {
  const next = nextBirthdayOnOrAfter(String(birthday ?? ""), todayYmd);
  if (!next) return null;
  const [y, m, d] = next.split("-").map(Number);
  // Subtract the lead days as CALENDAR days before converting, so a lead that
  // crosses a DST boundary still lands on REMINDER_HOUR local, not an hour off.
  const shifted = new Date(Date.UTC(y, m - 1, d - leadDays));
  return zonedWallClockToUtc(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
    REMINDER_HOUR,
    0,
    timeZone,
  );
}

/** The soonest run across every contact, skipping any birthday that won't parse. */
export function soonestRunAt(
  contacts: ReadonlyArray<{ birthday?: unknown; lead_days?: unknown }>,
  todayYmd: string,
  timeZone: string,
): Date | null {
  let soonest: Date | null = null;
  for (const c of contacts) {
    const lead = typeof c?.lead_days === "number" && Number.isFinite(c.lead_days) && c.lead_days >= 0
      ? c.lead_days
      : DEFAULT_LEAD_DAYS;
    const at = contactRunAt(String(c?.birthday ?? ""), lead, todayYmd, timeZone);
    // A stored birthday this cannot parse is skipped rather than crashing the
    // save — it simply gets no vote on when the row next runs, which is what
    // the app's own version does.
    if (at && (!soonest || at < soonest)) soonest = at;
  }
  return soonest;
}

/** Already on the list? Matched on the normalised name, like everywhere else. */
export function hasContactNamed(
  contacts: ReadonlyArray<{ name?: unknown }>,
  person: string,
): boolean {
  const wanted = normPerson(person);
  if (!wanted) return false;
  return contacts.some((c) => normPerson(String(c?.name ?? "")) === wanted);
}

export async function appendBirthdayContact(
  client: BirthdayTaskClient,
  userId: string,
  person: string,
  birthdayMmDd: string,
  todayYmd: string,
  timeZone: string,
): Promise<BirthdayContactOutcome> {
  const contact = {
    name: person,
    birthday: birthdayMmDd,
    relationship: "",
    lead_days: DEFAULT_LEAD_DAYS,
  };

  // ⚠️ #1743 — read-modify-write, so it retries. Two things can send it round
  // again: a 23505 from the unique index (another writer created the row) and
  // a zero-row compare-and-set (another writer changed the contacts array).
  // Both mean "re-read and append to what is actually there", and both used to
  // silently lose her contact instead.
  for (let attempt = 0; attempt <= SINGLE_ROW_RETRY_LIMIT; attempt++) {
  try {
    const { data: rows, error: selErr } = await client
      .from("scheduled_tasks")
      // updated_at comes back so the write below can compare-and-set on it.
      .select("id, config, updated_at")
      .eq("user_id", userId)
      .eq("task_type", "birthday_reminder")
      .limit(1);
    if (selErr) {
      console.error("[create_event] birthday_reminder lookup failed:", selErr);
      return { ok: false, reason: "lookup" };
    }

    const existing = Array.isArray(rows) ? rows[0] : null;

    if (existing?.id) {
      const current: Array<Record<string, unknown>> = Array.isArray(existing.config?.contacts)
        ? existing.config.contacts
        : [];
      if (hasContactNamed(current, person)) {
        // Saying the same birthday twice must not double it.
        return { ok: true, created: false, alreadyThere: true };
      }
      const updated = [...current, contact];
      const soonest = soonestRunAt(updated, todayYmd, timeZone);
      const patch: Record<string, unknown> = {
        // ⚠️ SPREAD, not replace (#1354). Writing `{ contacts }` alone drops
        // every other key in that jsonb — `e2e_marker` today, and whatever
        // else tomorrow. Both of the app's birthday paths were the odd ones
        // out here once already.
        config: { ...(existing.config || {}), contacts: updated },
        updated_at: new Date().toISOString(),
      };
      // Only move the wake-up if this contact is genuinely sooner; a null
      // means nothing parsed, and overwriting a good timestamp with null
      // would silence every contact on the row.
      if (soonest) patch.next_run_at = soonest.toISOString();

      // House rule 16 — an UPDATE that matches no row (RLS, a stale id)
      // returns 200 with error === null.
      //
      // ⚠️ #1743 — COMPARE-AND-SET on updated_at. A unique index stops a
      // second ROW; it says nothing about two appends that both read this
      // same contacts array and both write it back, where the second write
      // silently drops the first contact. Matching on the updated_at we read
      // means a write that lost that race affects ZERO rows instead of
      // clobbering, and the caller re-reads and appends to what is actually
      // there.
      //
      // Both writers of this row set updated_at explicitly (there is no
      // trigger doing it), which is what makes it usable as the version.
      const { data, error } = await client
        .from("scheduled_tasks")
        .update(patch)
        .eq("id", existing.id)
        .eq("updated_at", existing.updated_at)
        .select("id");
      if (error) {
        console.error("[create_event] birthday contact update failed:", error);
        return { ok: false, reason: "write" };
      }
      if (!Array.isArray(data) || data.length === 0) {
        // Either someone else wrote first (stale updated_at) or the row is
        // gone. Re-read and try again rather than reporting a failure over a
        // contact that can still be saved.
        continue;
      }
      return { ok: true, created: false, alreadyThere: false };
    }

    const soonest = soonestRunAt([contact], todayYmd, timeZone);
    const { data, error } = await client
      .from("scheduled_tasks")
      .insert({
        user_id: userId,
        task_type: "birthday_reminder",
        // Stated rather than inherited from the column default (#479), and
        // identical to what the app writes. Derived from REMINDER_HOUR so it
        // cannot drift from the next_run_at computed above.
        schedule_time: SCHEDULE_TIME,
        display_name: "Birthday Reminders",
        config: { contacts: [contact] },
        next_run_at: soonest ? soonest.toISOString() : null,
        is_active: true,
      })
      .select("id")
      .single();
    if (isUniqueViolation(error)) {
      // ⚠️ #1743 — the partial unique index did its job: another writer
      // created this user's row between our select and our insert. That is a
      // race we WON the wrong side of, not a failure she should hear about —
      // she just added a birthday. Go round again and append to the row that
      // won.
      continue;
    }
    if (error || !data?.id) {
      console.error("[create_event] birthday_reminder insert failed:", error);
      return { ok: false, reason: "write" };
    }
    return { ok: true, created: true, alreadyThere: false };
  } catch (e) {
    console.error("[create_event] birthday contact write threw:", e);
    return { ok: false, reason: "threw" };
  }
  }
  // Retries exhausted — something is writing this row faster than we can read
  // it. Say so rather than confirming a contact that is not there.
  console.error("[create_event] birthday contact write lost every retry");
  return { ok: false, reason: "write" };
}
